import { lazy, Suspense, type ReactNode } from 'react';
import { HashRouter, Navigate, Route, Routes } from 'react-router';
import { AppShell } from '@/components/layout/AppShell';
import { Spinner } from '@/components/ui/misc';
import { useAuth } from '@/lib/auth';
import { LoginPage } from '@/features/auth/LoginPage';
import { InboxPage } from '@/features/inbox/InboxPage';
import { OrdersPage } from '@/features/orders/OrdersPage';
import { MenuPage } from '@/features/menu/MenuPage';
import { TemplatesPage } from '@/features/templates/TemplatesPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { UsersPage } from '@/features/users/UsersPage';
import { PromotionsPage } from '@/features/promotions/PromotionsPage';
import { CustomersPage } from '@/features/customers/CustomersPage';
// Recharts is heavy: load the dashboard only for owners who open it.
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));

function RequireAuth({ children, owner }: { children: ReactNode; owner?: boolean }) {
  const { status, user } = useAuth();
  if (status === 'loading') return <div className="flex h-full items-center justify-center"><Spinner className="size-6" /></div>;
  if (status === 'offline')
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <Spinner className="size-6" />
        <p className="font-medium">Can't reach the server</p>
        <p className="text-sm text-muted-foreground">Retrying automatically. Check the internet connection.</p>
      </div>
    );
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  if (owner && user?.role !== 'owner') return <Navigate to="/inbox" replace />;
  return <>{children}</>;
}

/** Hash routing: the same static build works on any web host and inside Tauri (tauri://localhost). */
export function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequireAuth><AppShell /></RequireAuth>}>
          <Route path="/inbox/:conversationId?" element={<InboxPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/dashboard" element={<RequireAuth owner><Suspense fallback={<div className="flex h-full items-center justify-center"><Spinner className="size-6" /></div>}><DashboardPage /></Suspense></RequireAuth>} />
          <Route path="/promotions" element={<RequireAuth owner><PromotionsPage /></RequireAuth>} />
          <Route path="/customers" element={<RequireAuth owner><CustomersPage /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth owner><SettingsPage /></RequireAuth>} />
          <Route path="/users" element={<RequireAuth owner><UsersPage /></RequireAuth>} />
        </Route>
        <Route path="*" element={<Navigate to="/inbox" replace />} />
      </Routes>
    </HashRouter>
  );
}
